import { useCallback, useEffect, useState } from 'react';
import {
  useAllCapabilitiesForUserQuery,
  useAllServicesForUserQuery,
  useSelfQuery,
  useUserByIdQuery,
} from '../../hooks/useRequest.generated';
import {
  ICapabilitiesForUser,
  IOrganizationService,
  ISelf,
  Maybe,
} from '../../types/types.generated';
import { IAuthUser } from '../../types/authUser';
import { buildAuthUser } from '../utils/authUserUtils';

interface IAuthUserServiceProps {
  loadUserOnSuccess: () => void;
  loadUserOnFailure: () => void;
}

/**
 * Hook to get the populate the current Authenticated user information based on various sources.
 * Don't use directly but instead use the useAuthUser hook with the AuthUser Context.
 */
export const useAuthUserFetcher = ({
  loadUserOnSuccess,
  loadUserOnFailure,
}: IAuthUserServiceProps) => {
  const [authUser, setAuthUser] = useState<Maybe<IAuthUser>>(null);
  const [loadUser, setLoadUser] = useState(false);

  const {
    isSuccess: selfIsSuccess,
    isError: selfIsError,
    data: selfRes,
  } = useSelfQuery({}, { enabled: loadUser });

  const {
    data: userData,
    isSuccess: userDataIsSuccess,
    isError: userDataIsError,
  } = useUserByIdQuery(
    { idOrPID: selfRes?.self?.id },
    /**
     * Held until the id it needs exists.
     *
     * Ungated, this ran immediately with `idOrPID: undefined` — a request that
     * cannot identify anyone — and then again once `self` resolved. Two different
     * query keys, so react-query treated them as separate queries and both hit
     * the network, which is why `userById` appeared twice on every page load.
     */
    { enabled: loadUser && !!selfRes?.self?.id }
  );

  const {
    isSuccess: capabilitiesIsSuccess,
    isError: capabilitiesIsError,
    data: capabilitiesRes,
  } = useAllCapabilitiesForUserQuery({}, { enabled: loadUser });

  const {
    isSuccess: userServicesIsSuccess,
    isError: userServicesIsError,
    data: userServicesRes,
  } = useAllServicesForUserQuery({}, { enabled: loadUser });

  const self: Maybe<ISelf> = selfRes?.self || null;

  const capabilities: Maybe<ICapabilitiesForUser> =
    capabilitiesRes?.allCapabilitiesForUser || null;

  const userServices: Maybe<IOrganizationService[]> =
    userServicesRes?.allServicesForUser || null;

  const isError = selfIsError || capabilitiesIsError || userServicesIsError;

  useEffect(() => {
    if (
      selfIsSuccess &&
      self &&
      capabilitiesIsSuccess &&
      capabilities &&
      userServicesIsSuccess &&
      userServices
    ) {
      const userAccessPolicies = userData?.userById?.accessPolicyIds || [];
      const curAuthUser = buildAuthUser(
        self,
        capabilities,
        userServices,
        userAccessPolicies
      );

      setAuthUser(curAuthUser);
      loadUserOnSuccess();
    }

    if (isError) {
      loadUserOnFailure();
    }
  }, [
    capabilities,
    capabilitiesIsSuccess,
    isError,
    loadUserOnFailure,
    loadUserOnSuccess,
    self,
    selfIsSuccess,
    userServices,
    userServicesIsSuccess,
    userDataIsSuccess,
  ]);

  /**
   * Once the auth session is authenticated via PI, populate the current auth user bu calling various sources.
   */
  const loadAuthUser = useCallback((): void => {
    setLoadUser(true);
  }, []);

  /**
   * Clear the auth user on session sign-out so it can start all over again.
   */
  const clearAuthUser = useCallback((): void => {
    setLoadUser(false);
  }, []);

  return {
    authUser,
    loadAuthUser,
    clearAuthUser,
  };
};
